import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {captureException, captureMessage} from '../utils/SentryUtils';
import settings from '../../config/settings';
import ModalOverlay from './ModalOverlay';
import PopoverWidget from './PopoverWidget';
import * as BrowserUtils from '../utils/BrowserUtils';
import { subscribeToVisibilityChangeEvent } from '../utils/VisibilityEventUtils';
import * as CardUtils from '../utils/CardUtils';
import PubSub from '../utils/PubSub';
import ClientAnalytics from '../analytics/ClientAnalytics';
import { actionModes, displayModes, renderModes } from '../const/AugmentationConst';
import { cardStyles } from '../const/VuCardConst';
import RendererMessages from '../const/RendererMessages';
import InlineFrame from './InlineFrame';
import urlJoin from 'url-join';
import urlParse from 'url-parse';
import qs from 'qs';
import _ from 'lodash';
import { injectHtml } from '../utils/DomUtils';
import { ImagePropType } from '../utils/ApiPropTypes';
import { getImageUrl } from '../utils/ApiUtils';
import autobind from 'autobind-decorator';
import FrameMessages from '../const/FrameMessages';
import { AMAZON_AD_AFFILIATE_TYPE } from '../ad/const';
import * as CampaignUtils from '../utils/CampaignUtils';
import { fetchAdIframeUrl } from '../ad/utils';
import CommerceUnit from './CommerceUnit';

export default class CampaignRenderer extends React.Component {
  constructor() {
    super();

    this.trackingCodeContainer = null;
    this.wasDisplayTracked = false;
    this.root = null;
    this.trigger = null;
    this.cachedProductDataSources = {};
    this.adRefreshTimer = null;
    this.productRefreshTimer = null;

    this.state = {
      isActive: false,
      canDisplay: false,
      adsRefreshTimer: null,
      productRefreshTimer: null,
    };

    this.deviceType = '';

    this.unsubscribeVisibility = subscribeToVisibilityChangeEvent(this.onVisibilityChange);
  }

  componentWillUnmount() {
    if (this.unsubscribeVisibility) {
      this.unsubscribeVisibility.unsubscribe()
    }
  }

  async componentDidMount() {
    this.deviceType = BrowserUtils.getDeviceType();

    this.displayPopup();

    PubSub.subscribe('widgetTriggered', (widget) => {
      if (widget !== this) {
        this.setState({ isPopupOpen: false });
      }
    });

    PubSub.subscribe(RendererMessages.notifyFrameHidden, (frameId) => {
      if (frameId === this.props.frameId) {
        this.setState({ isPopupOpen: false });
      }
    });

    const shouldApplyLoadPixels = ![renderModes.iframeVupopup, renderModes.iframeBottomBanner].includes(this.props.renderMode) && !this.shouldForwardImpressionTrackingToAdComponent();
    if (shouldApplyLoadPixels) {
      this.applyTrackingCodes('load');
    }
    this.setupDisplayDuration();
  }

  @autobind
  setupDisplayDuration() {
    const { displayDuration } = this.props;
    if (!displayDuration) {
      return;
    }
    setTimeout(this.handleDisplayDurationElapsed, displayDuration * 1000);
  }

  @autobind
  handleDisplayDurationElapsed() {
    const { rendererId, slotId, frameId } = this.props;
    PubSub.publish(RendererMessages.hideRenderer, { rendererId, slotId });
    if (frameId) {
      PubSub.publish(RendererMessages.hideFrame, frameId);
    }
  }

  async displayPopup() {
    const {
      renderMode, rendererId, usePreLoadedIframe, frameId, record, vucardStyle, adSize,
    } = this.props;

    if (this.isNonCardPopup()) {
      // Affiliate Ad popup/banner iframes
      const isIframePopup = [renderModes.iframeVupopup, renderModes.iframeBottomBanner].includes(renderMode);
      const isNonIframePopup = [renderModes.nonIframeVupopup, renderModes.nonIframeBottomBanner].includes(renderMode);

      if (isIframePopup) {
        const iframeUrl = this.getAdFrameUrl();
        try {
          await fetchAdIframeUrl(iframeUrl);
        } catch (e) {
          this.hideRendererOnError();
          captureMessage(`Error while loading Ad iframe url: ${iframeUrl}`, 'error');
          return;
        }
      }

      if (isIframePopup || isNonIframePopup) {
        this.setState({ isPopupOpen: true });
      }
      if (isIframePopup) {
        const iframeParams = { frameId, frameUrl: this.getAdFrameUrl(), renderMode, adSize };
        PubSub.publish(RendererMessages.addAdFrame, iframeParams);
      }
    } else if (usePreLoadedIframe) {
      // VuCard based iframes
      if (this.isOverlayMode()) {
        const frameUrl = this.getVuCardFrameUrl();
        const hasVideo = !!CardUtils.getPosterVideoUrl(record);
        const iframeParams = { frameId, frameUrl, hasVideo, vucardStyle, rendererId };
        PubSub.publish(RendererMessages.addCardFrame, iframeParams);
      }
    }

    if ([renderModes.vupopup, renderModes.iframeVupopup, renderModes.nonIframeVupopup].includes(renderMode)) {
      this.setState({ isPopupOpen: true });
      PubSub.publish(RendererMessages.displayFrame, this.props.frameId);
    }
  }

  componentWillUnmount() {
    const { frameId } = this.props;
    window.parent.postMessage({ title: FrameMessages.requestHide, frameId }, '*');
  }

  /**
   * @returns Is this renderer a VuCard popup
   */
  isNonCardPopup() {
    return this.isPopup() && this.props.renderMode !== renderModes.vupopup;
  }

  isPopup() {
    return [renderModes.vupopup, renderModes.iframeVupopup, renderModes.iframeBottomBanner, renderModes.nonIframeVupopup, renderModes.nonIframeBottomBanner].includes(this.props.renderMode);
  }

  isOverlayMode() {
    return this.props.actionMode === actionModes.overlay;
  }

  trackTriggerDisplay() {
    if (!this.wasDisplayTracked) {
      const {
        renderMode, displayMode, cardId, campaignId, collectionId, rendererId, showsPageId,
        record
      } = this.props;
      const phraseText = this.props.phraseText || undefined;

      ClientAnalytics.sendTriggerDisplayed(
        renderMode,
        displayMode,
        this.getProgramId(record),
        record.title,
        rendererId,
        cardId,
        campaignId,
        collectionId,
        showsPageId,
        phraseText);

      this.wasDisplayTracked = true;
    }
  }

  @autobind
  onVisibilityChange() {
    const isVisible = (this.root && BrowserUtils.isElementVisibleInViewport(this.root)) ||
      (this.trigger && BrowserUtils.isElementVisibleInViewport(this.trigger));

    if (isVisible) {
      this.trackTriggerDisplay();
    }
  }

  isInCommerceUnitMode() {
    const { renderMode, adTemplate, adRenderer } = this.props;
    return adRenderer && !adTemplate && [renderModes.vuline, renderModes.nonIframeVupopup, renderModes.nonIframeBottomBanner].includes(renderMode);
  }

  render() {
    return (
      <span
        ref={(root) => {
          this.root = root;
        }}
        className="vuCardTrigger"
      >
        {
          this.renderRendererContent()
        }
      </span>
    );
  }

  renderRendererContent() {
    return (
      <React.Fragment>
        {this.renderTrigger()}
        {this.renderPopup()}
        {this.renderTrackingCodeContainer()}
      </React.Fragment>
    );
  }

  getAdTrackingId() {
    const {campaign, adTrackingId} = this.props;
    return adTrackingId || _.get(campaign, 'adTrackingId');
  }

  renderCommerceUnit() {
    const { adRefreshInterval,
      productRefreshInterval,
      programmaticAdTimeToShow,
      adRenderer,
      collectionId,
      collection,
      campaignId,
      rendererId,
      displayDuration,
    } = this.props;

    return (
      <CommerceUnit
        adRefreshInterval={adRefreshInterval}
        productRefreshInterval={productRefreshInterval}
        programmaticAdTimeToShow={programmaticAdTimeToShow}
        adRenderer={adRenderer}
        publisherId={collectionId}
        publisherName={collectionId}
        campaignId={campaignId}
        rendererId={rendererId}
        displayDuration={displayDuration}
        applyTrackingCodes={this.applyTrackingCodes}
        adTrackingId={this.getAdTrackingId()}
        publisherCssReset={collection.adCssReset}
      />
    );
  }

  @autobind
  setTriggerRef(trigger) {
    this.trigger = trigger;
  }

  @autobind
  renderTrigger() {
    const { displayMode, triggerMode } = this.props;
    let triggerProps = {};

    if (this.isPopup()) {
      return null;
    }

    if (this.isInCommerceUnitMode()) {
      return this.renderCommerceUnit();
    }

    switch (triggerMode) {
      case 'mouseover':
      default:
        triggerProps = {
          onClick: this.onClickTrigger,
          onMouseOver: this.onMouseOverTrigger
        };
        break;
      case 'click':
        triggerProps = { onClick: this.onClickTrigger };
        break;
    }

    switch (displayMode) {
      case displayModes.button:
      default:
        return this.renderButtonTrigger(triggerProps);
      case displayModes.poster:
        return this.renderPosterTrigger(triggerProps);
      case displayModes.text:
        return this.renderTextTrigger(triggerProps);
      case displayModes.phrase:
        triggerProps.phraseText = this.props.phraseText;
        return this.renderPhraseTrigger(triggerProps);
    }
  }

  renderButtonTrigger(triggerProps) {
    const buttonImgUrl = getImageUrl(this.props.triggerButton) || CardUtils.getDefaultTriggerButtonUrl();

    const props = {
      ...triggerProps,
      ...this.getTriggerLinkProps('vuCardTrigger-button')
    };

    return (
      <a {...props} ref={this.setTriggerRef}>
        <img src={buttonImgUrl} />
      </a>
    );
  }

  @autobind
  renderPosterTrigger(triggerProps) {
    let { displayMode, record, posterUrl } = this.props;
    posterUrl = posterUrl || CardUtils.getHorizontalPosterImageUrl(record, true);

    const props = {
      ...triggerProps,
      ...this.getTriggerLinkProps('vuCardTrigger-button')
    };

    if (!posterUrl) {
      return (
        <a {...props} ref={this.setTriggerRef}>
          <img src={settings.widget.triggerButtonImage} />
        </a>
      );
    }

    return (
      <a {...props} className={'vuCardTrigger-' + displayMode}>
        <img src={posterUrl} />
      </a>
    );
  }

  renderTextTrigger(triggerProps) {
    let { triggerText } = this.props;
    triggerText = triggerText || settings.widget.triggerText;

    const props = {
      ...triggerProps,
      ...this.getTriggerLinkProps('vuCardTrigger-link')
    };

    return (
      <span>
        <a {...props} ref={this.setTriggerRef}>
          {triggerText}
        </a>
      </span>
    );
  }

  renderPhraseTrigger(triggerProps) {
    const props = {
      ...triggerProps,
      ...this.getTriggerLinkProps('vuCardTrigger-link')
    };

    return (
      <span>
        <a {...props} ref={this.setTriggerRef}>
          {triggerProps.phraseText}
        </a>
      </span>
    );
  }

  getTriggerLinkUrl() {
    const { actionMode, actionUrl } = this.props;

    switch (actionMode) {
      default:
      case actionModes.overlay:
      case actionModes.standalone:
        return this.getVuCardStandaloneUrl();
      case actionModes.link:
        return actionUrl || this.getVuCardStandaloneUrl();
    }
  }

  getTriggerLinkProps(className) {
    const { triggerCustomClass } = this.props;

    const linkProps = {
      href: this.getTriggerLinkUrl(),
      className: cx([className], [triggerCustomClass])
    };

    const url = urlParse(linkProps.href, true);
    if (url && url.protocol !== 'tel:') {
      linkProps.target = '_blank';
    }
    return linkProps;
  }

  @autobind
  renderPopup() {
    const { isPopupOpen } = this.state;
    if (!isPopupOpen) {
      return null;
    }

    let {
      usePreLoadedIframe, vucardStyle, record, triggerMode, displayMode, rendererId, frameId, renderMode,
    } = this.props;

    if (renderMode === renderModes.iframeBottomBanner) {
      return null;
    }

    const isMobile = BrowserUtils.isMobile.any();
    triggerMode = isMobile ? 'click' : triggerMode;

    if (renderMode === renderModes.iframeVupopup) {
      return (
        <ModalOverlay rendererId={rendererId}
          renderMode={renderMode}
          frameId={frameId}
          skipTimeout={0}
          onClose={this.closePopup}
        />
      );
    }

    if (renderMode === renderModes.nonIframeVupopup) {
      return (
        <ModalOverlay rendererId={rendererId}
          renderMode={renderMode}
          skipTimeout={0}
          onClose={this.closePopup}
        >
          <div className={`vuCardTrigger__popup--${renderMode}`}>
            <a
              className="vupClosePopup vuCardTrigger__popup__close"
              onClick={this.closePopup}
            >
              <img
                className="vupClosePopup__icon"
                src={urlJoin(settings.frontendBaseURL, '/static/icons/ad_close_with_label.svg')}
                alt="Close Ad"
              />
            </a>
            <div className="vuCardTrigger__popup__content" >
              {
                this.renderCommerceUnit()
              }
            </div>
          </div>
        </ModalOverlay>
      );
    }

    if (renderMode === renderModes.nonIframeBottomBanner) {
      return (
        <div className={`vuCardTrigger__popup--${renderMode}`}>
          <a
            className="vupClosePopup"
            onClick={this.closePopup}
          >
            {'×'}
          </a>
          {
            this.renderCommerceUnit()
          }
        </div>
      );
    }

    switch (triggerMode) {
      case 'mouseover':
      default:
        return (
          <PopoverWidget rendererId={rendererId}
            hoverComponentDOM={this.root}
            displayMode={displayMode}
            onClose={this.closePopup}>
            {
              !usePreLoadedIframe &&
              <InlineFrame frameUrl={this.getVuCardFrameUrl()}
                vucardStyle={vucardStyle}
                hasVideo={!!CardUtils.getPosterVideoUrl(record)} />
            }
          </PopoverWidget>
        );
      case 'click':
        return (
          <ModalOverlay rendererId={rendererId}
            frameId={frameId}
            renderMode={renderMode}
            skipTimeout={0}
            onClose={this.closePopup}>
            <div className="vuCard-wrapper">
              {
                !usePreLoadedIframe &&
                <InlineFrame frameUrl={this.getVuCardFrameUrl()}
                  vucardStyle={vucardStyle}
                  hasVideo={!!CardUtils.getPosterVideoUrl(record)} />
              }
            </div>
          </ModalOverlay>
        );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { usePreLoadedIframe, frameId } = this.props;
    if (usePreLoadedIframe) {
      if (this.state.isPopupOpen && !prevState.isPopupOpen) {
        PubSub.publish(RendererMessages.displayFrame, frameId);
      }
    }
  }

  componentDidCatch(error) {
    this.hideRendererOnError();
    console.log(error);
    captureException(error);
  }

  hideRendererOnError() {
    const { rendererId, slotId, frameId } = this.props;
    PubSub.publish(RendererMessages.hideRenderer, { rendererId, slotId });
    if (frameId) {
      PubSub.publish(RendererMessages.hideFrame, frameId);
    }
  }

  getFbEventId(eventType) {
    const { userId, campaignId, collectionId, templateName } = this.props;
    return CampaignUtils.getFbPixelEventId(
      userId,
      collectionId,
      this.getAdTrackingId(),
      campaignId,
      templateName,
      eventType
    );
  }

  @autobind
  applyTrackingCodes(eventType) {
    if (eventType === 'load' && this.loadTrackingCodesApplied) {
      return;
    }

    const { trackingCodes } = this.props;
    let codes = '';
    if (_.isEmpty(trackingCodes)) {
      return;
    }
    const fbEventId = this.getFbEventId(eventType);
    trackingCodes.forEach((codeConfig) => {
      const code = CampaignUtils.getRendererTrackingCode(codeConfig, this.deviceType, eventType, fbEventId);
      if (code) {
        codes += code;
      }
    }
    );
    if (codes) {
      const wrapper = document.createElement('span');
      this.trackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, codes);
    }
    this.loadTrackingCodesApplied = true;
  }

  containsAdIframe() {
    const { vulineCallToAction, renderMode } = this.props;
    return renderMode === renderModes.vuline &&
      (vulineCallToAction && vulineCallToAction.indexOf('<iframe') >= 0);
  }

  shouldForwardImpressionTrackingToAdComponent() {
    // only for old mode using AdTemplate
    return this.containsAdIframe() && _.get(this.props, 'affiliateType.name') === AMAZON_AD_AFFILIATE_TYPE;
  }

  appendClickTrackingCodes() {
    const { trackingCodes } = this.props;
    let codes = '';
    const eventType = 'click';
    if (_.isEmpty(trackingCodes)) {
      return;
    }
    const fbEventId = this.getFbEventId(eventType);
    trackingCodes.forEach((codeConfig) => {
      const code = CampaignUtils.getRendererTrackingCode(codeConfig, this.deviceType, eventType, fbEventId);
      if (code) {
        codes += code;
      }
    }
    );
    if (codes) {
      const wrapper = document.createElement('span');
      this.trackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, codes);
    }
  }

  renderTrackingCodeContainer() {
    return (
      <span className="vuCardTrigger__trackingCodeContainer"
        style={{ position: 'absolute', bottom: '0' }}
        ref={(node) => {
          this.trackingCodeContainer = node;
        }}
      />
    );
  }

  createMarkup(html) {
    return { __html: html };
  }

  @autobind
  getVuCardFrameUrl() {
    const { record, cardId } = this.props;
    if (cardId) {
      return urlJoin(settings.cardFrameBaseURL, cardId, `?${qs.stringify(this.getVuCardFrameUrlQuery())}`);
    }
    return urlJoin(settings.recordFrameBaseURL, record.id);
  }

  @autobind
  getAdFrameUrl() {
    const { campaignId, rendererId } = this.props;
    return CampaignUtils.getPublisherIframeUrl(campaignId, rendererId);
  }

  getVuCardStandaloneUrl() {
    const { record, cardId } = this.props;
    if (cardId) {
      return urlJoin(settings.cardPageBaseURL, cardId, `?${qs.stringify(this.getVuCardStandaloneUrlQuery())}`);
    }
    return urlJoin(settings.recordPageBaseURL, record.id);
  }

  getVuCardFrameUrlQuery() {
    const { renderMode, rendererId, campaignId, collectionId, showsPageId, usePreLoadedIframe } = this.props;
    const result = {
      rendererId,
      campaignId,
      collectionId,
      showsPageId,
      isPreloaded: (usePreLoadedIframe ? 1 : 0),
      renderMode,
    };

    return result;
  }

  getVuCardStandaloneUrlQuery() {
    const { rendererId, campaignId, collectionId, showsPageId } = this.props;
    return {
      rendererId,
      campaignId,
      collectionId,
      showsPageId
    };
  }

  @autobind
  onTrigger(trackingAction, e) {
    const {
      record, rendererId, cardId, campaignId, collectionId, showsPageId, displayMode, renderMode,
      onAfterTriggered
    } = this.props;
    const phraseText = this.props.phraseText || undefined;

    if (record) {
      if (!this.isOverlayMode() || this.shouldRedirect()) {
        ClientAnalytics.sendTriggeredUrl(
          renderMode,
          trackingAction,
          this.getTriggerLinkUrl(),
          this.getProgramId(record),
          record.title,
          rendererId,
          cardId,
          campaignId,
          collectionId,
          showsPageId,
          phraseText,
        );
      } else {
        e.preventDefault();
        this.setState({ isPopupOpen: true });
        PubSub.publish('widgetTriggered', this);

        ClientAnalytics.sendTriggered(
          renderMode,
          trackingAction,
          displayMode,
          this.getProgramId(record),
          record.title,
          rendererId,
          cardId,
          campaignId,
          collectionId,
          showsPageId,
          phraseText
        );
      }
      if (renderMode === renderModes.vuline) {
        this.appendClickTrackingCodes();
      }

      onAfterTriggered();
    }
  }

  onClickTrigger = this.onTrigger.bind(this, 'click');
  onMouseOverTrigger = this.onTrigger.bind(this, 'hover');

  shouldRedirect() {
    const event = {};
    this.props.onBeforeTrigger(event);
    return !!event.shouldRedirect;
  }

  @autobind
  closePopup() {
    this.setState({ isPopupOpen: false });
    if (this.props.renderMode !== renderModes.nonIframeVupopup) {
      PubSub.publish(RendererMessages.hideFrame, this.props.frameId);
    }
  }

  getProgramId(record) {
    return record.program ? record.program.id : record.id;
  }
}

CampaignRenderer.displayName = 'CampaignRenderer';
CampaignRenderer.propTypes = {
  record: PropTypes.object.isRequired,
  posterUrl: PropTypes.string, //optional custom one
  displayMode: PropTypes.oneOf(['button', 'text', 'poster', 'phrase']),
  triggerMode: PropTypes.oneOf(['click', 'mouseover']).isRequired,
  actionMode: PropTypes.oneOf(['overlay', 'standalone', 'link']).isRequired,
  actionUrl: PropTypes.string,
  triggerText: PropTypes.string,
  triggerButton: ImagePropType,
  triggerCustomClass: PropTypes.string,
  vucardStyle: PropTypes.string.isRequired,
  rendererId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number]),
  cardId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number]),
  campaignId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number]),
  collectionId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number]),
  showsPageId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number]),
  usePreLoadedIframe: PropTypes.bool,
  onBeforeTrigger: PropTypes.func,
  onAfterTriggered: PropTypes.func,
};

CampaignRenderer.defaultProps = {
  displayMode: displayModes.button,
  triggerMode: 'click',
  vucardStyle: cardStyles.classic,
  actionMode: actionModes.overlay,
  usePreLoadedIframe: true,
  onAfterTriggered: () => {
  },
  onBeforeTrigger: () => {
  }
};
